/**
 * Allega una CVE al report ENISA tramite typeahead sulle CVE note (stessa ricerca lato server
 * del ticket manuale, GET /cve/search: ≥3 caratteri, 300 ms di quiete, max 10 risultati).
 * La scelta fa POST /srp/reports/:id/cves (tracciato nel log audit). Navigabile da tastiera
 * (↑/↓/Invio/Esc).
 */

import { useId, useState } from 'react';
import { ApiError } from '../../api/client.js';
import { useKnownCveSearch } from '../triage/hooks.js';
import { CVE_SEARCH_MIN_QUERY, type KnownCve } from '../triage/types.js';
import { useAttachCve } from './hooks.js';
import triageStyles from '../triage/Triage.module.css';
import styles from './Srp.module.css';

/** Risultati mostrati per pagina di ricerca. */
const LIMITE_RISULTATI = 10;

function classeSeverita(severity: string | undefined): string {
  switch ((severity ?? '').toUpperCase()) {
    case 'CRITICAL':
      return triageStyles.badgeCritical ?? '';
    case 'HIGH':
      return triageStyles.badgeHigh ?? '';
    case 'MEDIUM':
      return triageStyles.badgeMedium ?? '';
    case 'LOW':
      return triageStyles.badgeLow ?? '';
    default:
      return triageStyles.badgeInfo ?? '';
  }
}

function messaggioErrore(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Errore sconosciuto';
}

export function SrpCvePicker({
  reportId,
  giaPresenti,
}: {
  reportId: number;
  /** CVE già nel report (mostrate come non selezionabili). */
  giaPresenti: string[];
}) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [aperto, setAperto] = useState(false);
  const [attivo, setAttivo] = useState(0);
  const ricerca = useKnownCveSearch(query, LIMITE_RISULTATI);
  const allega = useAttachCve(reportId);

  const presenti = new Set(giaPresenti.map((c) => c.toUpperCase()));
  const queryCorta = query.trim().length < CVE_SEARCH_MIN_QUERY;
  const items = queryCorta ? [] : (ricerca.data?.items ?? []);
  const inAttesa = !queryCorta && (ricerca.isFetching || ricerca.debouncedQuery !== query.trim());

  function scegli(cve: KnownCve) {
    if (presenti.has(cve.cveId.toUpperCase()) || allega.isPending) return;
    allega.mutate(
      {
        cveId: cve.cveId,
        ...(cve.severity ? { severity: cve.severity } : {}),
        knownExploited: cve.knownExploited,
      },
      {
        onSuccess: () => {
          setQuery('');
          setAperto(false);
          setAttivo(0);
        },
      },
    );
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAperto(true);
      setAttivo((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAttivo((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const scelta = items[attivo];
      if (aperto && scelta) scegli(scelta);
    } else if (e.key === 'Escape') {
      setAperto(false);
    }
  }

  return (
    <div className={styles.cvePicker} data-testid="srp-cve-picker">
      <label className={triageStyles.srOnly} htmlFor={`${listId}-input`}>
        Allega CVE
      </label>
      <div className={triageStyles.comboWrap}>
        <input
          id={`${listId}-input`}
          type="search"
          role="combobox"
          aria-expanded={aperto && items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={aperto && items[attivo] ? `${listId}-${attivo}` : undefined}
          className={styles.formInput}
          style={{ width: '100%' }}
          placeholder="Allega una CVE: cerca tra le CVE note (es. CVE-2024-3094)…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setAperto(true);
            setAttivo(0);
          }}
          onFocus={() => setAperto(true)}
          onBlur={() => setTimeout(() => setAperto(false), 150)}
          onKeyDown={onKeyDown}
          disabled={allega.isPending}
          data-testid="srp-cerca-cve"
        />

        {aperto && items.length > 0 && (
          <ul id={listId} role="listbox" className={triageStyles.comboList}>
            {items.map((c, i) => {
              const gia = presenti.has(c.cveId.toUpperCase());
              return (
                <li
                  key={c.cveId}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === attivo}
                  aria-disabled={gia}
                  className={`${triageStyles.comboOption} ${i === attivo ? triageStyles.comboOptionActive : ''} ${gia ? triageStyles.comboOptionDisabled : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    scegli(c);
                  }}
                  data-testid={`srp-opzione-cve-${c.cveId}`}
                >
                  <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.cveId}</span>
                    {c.severity && (
                      <span className={`${triageStyles.badge} ${classeSeverita(c.severity)}`}>
                        {c.severity}
                      </span>
                    )}
                    {c.knownExploited && (
                      <span
                        className={`${triageStyles.badge} ${triageStyles.badgeStateExploitable ?? ''}`}
                      >
                        KEV
                      </span>
                    )}
                    <span className={triageStyles.comboMeta}>{c.source}</span>
                    {gia && <span className={triageStyles.comboMeta}>già nel report</span>}
                  </span>
                  {c.description && <span className={triageStyles.comboDesc}>{c.description}</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {query.trim() !== '' && (
        <p className={triageStyles.pickerHint} aria-live="polite" data-testid="srp-stato-cerca-cve">
          {queryCorta
            ? `Ancora ${CVE_SEARCH_MIN_QUERY - query.trim().length} caratteri…`
            : inAttesa
              ? 'Ricerca…'
              : ricerca.isError
                ? messaggioErrore(ricerca.error)
                : items.length === 0
                  ? 'Nessuna CVE trovata.'
                  : ricerca.data?.hasMore
                    ? `Primi ${items.length} risultati: affina la ricerca per vederne altri.`
                    : `${items.length} risultati.`}
        </p>
      )}
      {allega.isError && (
        <span className={styles.inlineMutError} data-testid="errore-allega-cve">
          {messaggioErrore(allega.error)}
        </span>
      )}
    </div>
  );
}
